import { useEffect, useState } from "react";
import { Copy, Check, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";

function Typewriter({ text, speed = 18 }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    setI(0);
    if (!text) return;
    let idx = 0;
    const timer = setInterval(() => {
      idx += 1;
      setI(idx);
      if (idx >= text.length) clearInterval(timer);
    }, speed);
    return () => clearInterval(timer);
  }, [text, speed]);
  const shown = text ? text.slice(0, i) : "";
  const isDone = i >= (text?.length || 0);
  return (
    <span className="whitespace-pre-wrap">
      {shown}
      {!isDone && <span className="tq-caret" />}
    </span>
  );
}

export default function QuickMessageModal({
  open,
  onClose,
  message,
  generating,
  error,
  onRegenerate,
  onCopied,
}) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    if (!message) return;
    await navigator.clipboard.writeText(message);
    setCopied(true);
    onCopied?.();
    setTimeout(() => setCopied(false), 1800);
  };

  const charCount = (message || "").length;
  const lineCount = (message || "").split("\n").filter(Boolean).length;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="sm:max-w-[540px] rounded-xl border-0 p-0 overflow-hidden"
        style={{
          boxShadow: "var(--shadow-lg)",
        }}
        data-testid="quick-message-modal"
      >
        <div className="px-6 pt-6 pb-4">
          <DialogHeader>
            <DialogTitle className="text-[17px] font-semibold">
              Messaggio breve al cliente
            </DialogTitle>
            <DialogDescription
              className="text-[12px] mt-1"
              style={{ color: "var(--text-muted)" }}
            >
              Stile chat tecnica — 1 o 2 righe — chiede solo le info davvero
              mancanti.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-6 pb-4">
          {generating ? (
            <div
              className="flex items-center justify-center py-8 text-[14px]"
              style={{ color: "var(--text-muted)" }}
            >
              Stesura in corso...
            </div>
          ) : error ? (
            <div
              className="rounded-md px-3 py-2.5 text-[13px]"
              style={{
                background: "var(--semantic-red-bg)",
                color: "var(--semantic-red)",
                border: "1px solid #FECACA",
              }}
            >
              {error}
            </div>
          ) : message ? (
            <>
              <div
                className="rounded-md p-4 text-[14px] leading-[22px] font-medium min-h-[80px]"
                style={{
                  background: "var(--bg-app)",
                  border: "1px solid var(--border-subtle)",
                  color: "var(--text-primary)",
                }}
                data-testid="quick-message-text"
              >
                <Typewriter text={message} />
              </div>
              <div
                className="flex items-center justify-between mt-3 text-[11px] font-mono"
                style={{ color: "var(--text-muted)" }}
              >
                <span>
                  {charCount} caratteri · {lineCount}{" "}
                  {lineCount === 1 ? "riga" : "righe"}
                </span>
                <button
                  type="button"
                  onClick={onRegenerate}
                  className="transition-colors"
                  style={{ color: "var(--accent-primary)" }}
                >
                  Rigenera
                </button>
              </div>
            </>
          ) : (
            <div
              className="text-[14px] text-center py-4"
              style={{ color: "var(--text-muted)" }}
            >
              Nessun messaggio generato.
            </div>
          )}
        </div>

        <DialogFooter
          className="px-6 py-4 gap-2 sm:gap-2"
          style={{
            borderTop: "1px solid var(--border-subtle)",
            background: "var(--bg-app)",
          }}
        >
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-[13px] font-medium transition-colors"
            style={{ color: "var(--text-secondary)" }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "var(--bg-hover)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "transparent")
            }
            data-testid="quick-message-close"
          >
            <X className="h-3.5 w-3.5" />
            Chiudi
          </button>
          <button
            type="button"
            onClick={copy}
            disabled={!message}
            className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md text-[13px] font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            style={{
              background: "var(--accent-primary)",
              color: "var(--text-on-accent)",
              boxShadow: "var(--shadow-sm)",
            }}
            onMouseEnter={(e) => {
              if (!e.currentTarget.disabled)
                e.currentTarget.style.background = "var(--accent-hover)";
            }}
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "var(--accent-primary)")
            }
            data-testid="quick-message-copy"
          >
            {copied ? (
              <>
                <Check className="h-3.5 w-3.5" /> Copiato
              </>
            ) : (
              <>
                <Copy className="h-3.5 w-3.5" /> Copia negli appunti
              </>
            )}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
